import { useEffect, useMemo, useState } from 'react';
import { Network } from 'lucide-react';
import type { CompetitionDrawDivision } from '@tark/shared/lib';

export function CompetitionPublicDraw({ divisions }: { divisions: CompetitionDrawDivision[] }) {
  const [divisionId, setDivisionId] = useState(divisions[0]?.divisionPublicId ?? '');
  useEffect(() => {
    if (!divisions.some((division) => division.divisionPublicId === divisionId)) {
      setDivisionId(divisions[0]?.divisionPublicId ?? '');
    }
  }, [divisionId, divisions]);
  const selected = useMemo(
    () => divisions.find((division) => division.divisionPublicId === divisionId) ?? divisions[0],
    [divisionId, divisions],
  );
  if (!selected) return null;

  return (
    <section className="mt-10 border-y border-border py-6" aria-labelledby="public-draw-title">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Network className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 id="public-draw-title" className="text-lg font-semibold text-text">Draw</h2>
          <p className="text-sm text-text-muted">Round-robin matchups</p>
        </div>
      </div>

      {divisions.length > 1 ? (
        <div className="mt-5 flex gap-2 overflow-x-auto border-b border-border" role="tablist" aria-label="Draw divisions">
          {divisions.map((division) => (
            <button
              key={division.divisionPublicId}
              type="button"
              role="tab"
              aria-selected={division.divisionPublicId === selected.divisionPublicId}
              className={`shrink-0 border-b-2 px-1 py-3 text-sm font-semibold ${division.divisionPublicId === selected.divisionPublicId ? 'border-primary text-primary' : 'border-transparent text-text-muted'}`}
              onClick={() => setDivisionId(division.divisionPublicId)}
            >
              {division.divisionName}
            </button>
          ))}
        </div>
      ) : (
        <p className="mt-5 text-sm font-semibold text-text">{selected.divisionName}</p>
      )}

      <div className="mt-5 grid gap-5 overflow-x-auto pb-2 md:grid-flow-col md:auto-cols-[minmax(220px,1fr)]">
        {selected.rounds.map((round) => (
          <div key={round.roundNumber} className="border-l border-border pl-4 first:border-l-0 first:pl-0">
            <p className="mb-3 text-xs font-semibold uppercase text-text-muted">Round {round.roundNumber}</p>
            <div className="space-y-3">
              {round.matches.map((match) => (
                <div key={`${round.roundNumber}-${match.matchNumber}`} className="border-b border-border pb-3 last:border-b-0">
                  <p className="text-sm font-semibold text-text">{names(match.sideA.displayNames)}</p>
                  <p className="my-1 text-xs font-semibold text-text-muted">vs</p>
                  <p className="text-sm font-semibold text-text">{names(match.sideB.displayNames)}</p>
                </div>
              ))}
              {selected.byes.filter((bye) => bye.roundNumber === round.roundNumber).map((bye) => (
                <p key={`${round.roundNumber}-${names(bye.side.displayNames)}`} className="text-sm text-text-muted">
                  <span className="font-semibold text-text">{names(bye.side.displayNames)}</span> · Bye
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function names(values: string[]): string {
  return values.filter(Boolean).join(' / ') || 'Player unavailable';
}
